import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
    Box, Button, CircularProgress, Container, Grid, Paper, Stack,
    TextField, ToggleButton, ToggleButtonGroup, Typography, Alert
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logger from "../../services/Logger";
import CustomSnackbar from "../components/CustomSnackbar";
import generatePiecePaths from "./generatePiecePaths";
import JigsawTemplateService from "./JigsawTemplateService";

const TILE_OPTIONS = [6, 9, 12, 16, 24, 32];
const DEFAULT_TILE_SIZE = 6;

// LocalStorage keys
const STORAGE_KEYS = {
    TILE_SIZE: 'admin.jigsawTemplate_tileSize',
    SEED: 'admin.jigsawTemplate_seed',
    TAB_SIZE: 'admin.jigsawTemplate_tabSize',
    JITTER: 'admin.jigsawTemplate_jitter'
};

// Helper functions for localStorage
const saveToStorage = (key, value) => {
    try {
        localStorage.setItem(key, String(value));
    } catch (e) {
        console.warn('Failed to save to localStorage:', e);
    }
};

const loadFromStorage = (key, defaultValue) => {
    try {
        const saved = localStorage.getItem(key);
        return saved !== null ? saved : defaultValue;
    } catch (e) {
        console.warn('Failed to load from localStorage:', e);
        return defaultValue;
    }
};

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

export default function CreateJigsawTemplatePage() {
    const navigate = useNavigate();
    const [tileSize, setTileSize] = useState(() => {
        const raw = loadFromStorage(STORAGE_KEYS.TILE_SIZE, String(DEFAULT_TILE_SIZE));
        const n = Number(raw);
        return TILE_OPTIONS.includes(n) ? n : DEFAULT_TILE_SIZE;
    });
    const [svgFile, setSvgFile] = useState(null);
    const [creating, setCreating] = useState(false);
    const [checking, setChecking] = useState(false);
    const [nameCheckResult, setNameCheckResult] = useState(null);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const fileRef = useRef(null);

    // Detail fields - load from localStorage
    const [name, setName] = useState("");
    const [seed, setSeed] = useState(() => loadFromStorage(STORAGE_KEYS.SEED, '8900'));
    const [tabSize, setTabSize] = useState(() => loadFromStorage(STORAGE_KEYS.TAB_SIZE, '22'));
    const [jitter, setJitter] = useState(() => loadFromStorage(STORAGE_KEYS.JITTER, '3'));

    // Auto-generate name based on parameters
    const generateName = useCallback(() => {
        const paddedTileSize = String(tileSize).padStart(2, '0');
        const paddedJitter = String(jitter).padStart(2, '0');
        return `T${paddedTileSize}-S${seed}-T${tabSize}-J${paddedJitter}`;
    }, [tileSize, seed, tabSize, jitter]);

    // Wrapper functions to save to localStorage when values change
    const updateTileSize = useCallback((value) => {
        setTileSize(value);
        saveToStorage(STORAGE_KEYS.TILE_SIZE, value);
    }, []);

    const updateSeed = useCallback((value) => {
        setSeed(value);
        saveToStorage(STORAGE_KEYS.SEED, value);
    }, []);

    const updateTabSize = useCallback((value) => {
        setTabSize(value);
        saveToStorage(STORAGE_KEYS.TAB_SIZE, value);
    }, []);

    const updateJitter = useCallback((value) => {
        setJitter(value);
        saveToStorage(STORAGE_KEYS.JITTER, value);
    }, []);

    // Update name whenever parameters change
    useEffect(() => {
        const autoName = generateName();
        setName(autoName);
        setNameCheckResult(null); // Clear previous check result when name changes
    }, [generateName]);

    const handleBack = () => {
        if (!creating) {
            navigate("/admin/jigsaw/template");
        }
    };

    const handleCheckName = useCallback(async () => {
        if (!name.trim()) {
            setError("Enter a name to check");
            return;
        }

        setChecking(true);
        setNameCheckResult(null);
        setError(null);

        try {
            const result = await JigsawTemplateService.checkNameDuplicated(name.trim());

            if (!result.success) {
                setError(result.error);
            } else {
                setNameCheckResult(result.data);
            }
        } catch (e) {
            Logger.error("CheckName:", e);
            setError(e.message || "Check failed");
        } finally {
            setChecking(false);
        }
    }, [name]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] ?? null;
        setSvgFile(file);
        setError(null);
    };

    const handleCreate = useCallback(async () => {
        if (!svgFile) { setError("Select an SVG file"); return; }
        setError(null);
        setCreating(true);

        try {
            const svgText = await svgFile.text();
            const pieces = generatePiecePaths(svgText, tileSize);
            const piecePathsJson = JSON.stringify({ pieces });

            // Build detail JSON
            const detail = {};
            if (seed.trim()) detail.seed = Number(seed);
            if (tabSize.trim()) detail.tabSize = Number(tabSize);
            if (jitter.trim()) detail.jitter = Number(jitter);
            const detailJson = JSON.stringify(detail);

            const formData = new FormData();
            formData.append("tileSize", String(tileSize));
            formData.append("piecePaths", piecePathsJson);
            formData.append("detail", detailJson);
            formData.append("svg", svgFile);
            if (name.trim()) formData.append("name", name.trim());

            const result = await JigsawTemplateService.create(formData);

            if (!result.success) {
                setError(result.error);
                setCreating(false);
                return;
            }

            setSvgFile(null);
            setNameCheckResult(null);
            if (fileRef.current) fileRef.current.value = "";
            setCreating(false);
            setSnackbar({ open: true, severity: "success", message: "Template created successfully!" });
        } catch (e) {
            Logger.error("CreateJigsawTemplateDialog:", e);
            setError(e.message || "Create failed");
            setCreating(false);
        }
    }, [svgFile, tileSize, seed, tabSize, jitter, navigate]);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Header with back button */}
            <Box sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    disabled={creating}
                    sx={{ mb: 2 }}
                >
                    Back to Templates
                </Button>
                <Typography variant="h4" component="h1">
                    Create Jigsaw Template
                </Typography>
            </Box>

            {/* Main content */}
            <Paper sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    {/* Left: main fields */}
                    <Grid item xs={12} md={7}>
                        <Stack spacing={2.5}>
                            {/* Name */}
                            <Box>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                    <TextField
                                        label="Name (Auto-generated)"
                                        value={name}
                                        disabled={creating}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                backgroundColor: 'action.hover',
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleCheckName}
                                        disabled={creating || checking || !name.trim()}
                                        sx={{ minWidth: 80, height: 40 }}
                                    >
                                        {checking ? <CircularProgress size={16} /> : "Check"}
                                    </Button>
                                </Box>
                                {nameCheckResult && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 0.5,
                                            color: nameCheckResult.duplicated ? "error.main" : "success.main",
                                            fontWeight: 500
                                        }}
                                    >
                                        {nameCheckResult.duplicated ? "⚠️ Name already exists" : "✅ Name is available"}
                                    </Typography>
                                )}
                            </Box>

                            {/* Tile size toggle */}
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Tile Size</Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    value={tileSize}
                                    onChange={(_, v) => { if (v != null) updateTileSize(v); }}
                                    size="small"
                                    disabled={creating}
                                    sx={{ flexWrap: "wrap", gap: 0.5 }}
                                >
                                    {TILE_OPTIONS.map((g) => (
                                        <ToggleButton key={g} value={g} sx={{ px: 2 }}>
                                            {g}×{g}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>

                            {/* SVG file picker */}
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>SVG File</Typography>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    disabled={creating}
                                    size="small"
                                >
                                    Choose SVG file
                                    <VisuallyHiddenInput
                                        ref={fileRef}
                                        type="file"
                                        accept=".svg,image/svg+xml"
                                        onChange={handleFileChange}
                                    />
                                </Button>
                                {svgFile && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        {svgFile.name}{" "}
                                        <Typography component="span" variant="caption" color="text.secondary">
                                            ({(svgFile.size / 1024).toFixed(1)} KB)
                                        </Typography>
                                    </Typography>
                                )}
                            </Box>

                            {error && (
                                <Typography variant="body2" color="error">{error}</Typography>
                            )}

                            <Typography variant="caption" color="text.secondary">
                                The SVG die lines will be parsed to generate piece clip-paths automatically.
                            </Typography>
                        </Stack>
                    </Grid>

                    {/* Right: detail section */}
                    <Grid item xs={12} md={5}>
                        <Box sx={{ pl: { md: 2 }, borderLeft: { md: 1 }, borderColor: { md: "divider" }, height: "100%" }}>
                            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>Detail</Typography>
                            <Stack spacing={2}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                    <TextField
                                        label="Seed"
                                        type="number"
                                        value={seed}
                                        onChange={(e) => updateSeed(e.target.value)}
                                        disabled={creating}
                                        size="small"
                                        sx={{ width: 100 }}
                                        slotProps={{ htmlInput: { min: 0, max: 9999 } }}
                                    />
                                    <ToggleButtonGroup size="small">
                                        {[3200, 6400, 8900].map((v) => (
                                            <ToggleButton key={v} value={v} onClick={() => updateSeed(String(v))} selected={seed === String(v)} disabled={creating} sx={{ px: 1 }}>
                                                {v}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                    <TextField
                                        label="Tab Size (%)"
                                        type="number"
                                        value={tabSize}
                                        onChange={(e) => updateTabSize(e.target.value)}
                                        disabled={creating}
                                        size="small"
                                        sx={{ width: 100 }}
                                        slotProps={{ htmlInput: { min: 5, max: 30 } }}
                                    />
                                    <ToggleButtonGroup size="small">
                                        {[22, 27].map((v) => (
                                            <ToggleButton key={v} value={v} onClick={() => updateTabSize(String(v))} selected={tabSize === String(v)} disabled={creating} sx={{ px: 1 }}>
                                                {v}%
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                    <TextField
                                        label="Jitter (%)"
                                        type="number"
                                        value={jitter}
                                        onChange={(e) => updateJitter(e.target.value)}
                                        disabled={creating}
                                        size="small"
                                        sx={{ width: 100 }}
                                        slotProps={{ htmlInput: { min: 0, max: 13 } }}
                                    />
                                    <ToggleButtonGroup size="small">
                                        {[3, 7, 11].map((v) => (
                                            <ToggleButton key={v} value={v} onClick={() => updateJitter(String(v))} selected={jitter === String(v)} disabled={creating} sx={{ px: 1 }}>
                                                {v}%
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                                Sent as JSON in the <code>detail</code> field.
                            </Typography>
                            <Alert severity="success" sx={{ mt: 2 }}>
                                Full size : 1536 x 1536
                            </Alert>
                        </Box>
                    </Grid>
                </Grid>

                {/* Action buttons */}
                <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    <Button onClick={handleBack} disabled={creating} size="large">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreate}
                        variant="contained"
                        disabled={creating || !svgFile}
                        size="large"
                        sx={{ minWidth: 120 }}
                    >
                        {creating ? <CircularProgress size={20} /> : "Create Template"}
                    </Button>
                </Box>
            </Paper>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(o) => setSnackbar((s) => ({ ...s, open: o }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Container>
    );
}
